import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Button,
  Tag,
  Tooltip,
  Badge,
  Select as AntSelect,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  CrownOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";
import { authHeaders,getSessionToken } from "../utils/session";

const PAGE_SIZE = 10;

const statCard = (icon, label, value, color, bg) => (
  <div
    style={{
      background: bg,
      borderRadius: 14,
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      flex: 1,
      minWidth: 180,
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>{value}</div>
    </div>
  </div>
);

const statusTag = (status) => {
  const map = {
    active: { color: "#16a34a", bg: "#dcfce7", label: "Active" },
    expired: { color: "#dc2626", bg: "#fee2e2", label: "Expired" },
    cancelled: { color: "#9ca3af", bg: "#f3f4f6", label: "Cancelled" },
  };
  const s = map[status?.toLowerCase()] || map.active;
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: s.color,
        background: s.bg,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {status?.toLowerCase() === "active" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
      {s.label}
    </span>
  );
};

const PaidUsers = () => {
  const navigate = useNavigate();
  const token = getSessionToken();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    revenue: "0",
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/get-user-list`, {
        params: {
          page,
          limit: PAGE_SIZE,
          search,
          subscription_type: "paid",
          status: statusFilter || undefined,
        },
        headers: { ...authHeaders() },
      });
      const d = res.data;
      if (d.status === 200 || d.status === 201 || d.success) {
        const list = d.data || d.users || [];
        setUsers(list);
        setTotal(d.total || d.pagination?.total || list.length);
        setStats({
          total: d.total || list.length,
          active: d.active_count || list.filter((u) => u.status === "active" || u.status === 1).length,
          expired: d.expired_count || list.filter((u) => u.status === "expired").length,
          revenue: d.total_revenue || "—",
        });
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleSearch = () => setSearch(searchInput);

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const cols = [
    { label: "#", width: 48 },
    { label: "Customer", width: 200 },
    { label: "Email", width: 200 },
    { label: "Plan", width: 130 },
    { label: "Amount", width: 110 },
    { label: "Start Date", width: 120 },
    { label: "End Date", width: 120 },
    { label: "Status", width: 110 },
    { label: "Actions", width: 90 },
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 18,
                    }}
                  >
                    <CrownOutlined />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: "#1e293b" }}>
                      Paid Users
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                      Customers with active paid subscriptions
                    </p>
                  </div>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/cms/home">CMS</Link>
                  </li>
                  <li className="breadcrumb-item active">Paid Users</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 24,
            }}
          >
            {statCard(<CrownOutlined />, "Total Paid Users", stats.total, "linear-gradient(135deg,#f59e0b,#d97706)", "#fffbeb")}
            {statCard(<CheckCircleOutlined />, "Active", stats.active, "linear-gradient(135deg,#22c55e,#16a34a)", "#f0fdf4")}
            {statCard(<CloseCircleOutlined />, "Expired", stats.expired, "linear-gradient(135deg,#f87171,#dc2626)", "#fef2f2")}
            {statCard(<DollarOutlined />, "Total Revenue", stats.revenue, "linear-gradient(135deg,#6366f1,#4f46e5)", "#eef2ff")}
          </div>

          {/* Table card */}
          <Card
            style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Toolbar */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Input
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 260, borderRadius: 8 }}
                allowClear
                onClear={() => { setSearchInput(""); setSearch(""); }}
              />
              <AntSelect
                placeholder="All Statuses"
                value={statusFilter || undefined}
                onChange={(v) => setStatusFilter(v || "")}
                allowClear
                style={{ width: 150, borderRadius: 8 }}
                options={[
                  { value: "active", label: "Active" },
                  { value: "expired", label: "Expired" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />
              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  background: "linear-gradient(135deg,#f59e0b,#d97706)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{ borderRadius: 8 }}
              >
                Reset
              </Button>
              <div style={{ marginLeft: "auto", color: "#64748b", fontSize: 13 }}>
                Total: <b>{total}</b> paid users
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {cols.map((c) => (
                      <th
                        key={c.label}
                        style={{
                          padding: "12px 14px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#475569",
                          fontSize: 12,
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                          width: c.width,
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#94a3b8" }}>
                        <div style={{ fontSize: 16 }}>Loading...</div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: 48 }}>
                        <div style={{ color: "#94a3b8", fontSize: 15 }}>
                          <CrownOutlined style={{ fontSize: 36, marginBottom: 12, display: "block" }} />
                          No paid users found
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr
                        key={user.id || idx}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                      >
                        <td style={{ padding: "12px 14px", color: "#64748b" }}>
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              {(user.name || user.full_name || "U")[0]?.toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: "#1e293b" }}>
                                {user.name || user.full_name || "—"}
                              </div>
                              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                ID: {user.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 14px", color: "#475569" }}>
                          {user.email || "—"}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <Tag color="gold" style={{ borderRadius: 6, fontWeight: 600 }}>
                            {user.plan_name || user.subscription_plan || "Paid"}
                          </Tag>
                        </td>
                        <td style={{ padding: "12px 14px", fontWeight: 600, color: "#16a34a" }}>
                          ₹{user.amount || user.plan_price || "—"}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#475569" }}>
                          {user.subscription_start_date || user.start_date || "—"}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#475569" }}>
                          {user.subscription_end_date || user.end_date || "—"}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          {statusTag(user.subscription_status || (user.status === 1 ? "active" : "expired"))}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <Tooltip title="View Profile">
                            <button
                              onClick={() => navigate(`/cms/manageUserData/user-profile/${user.id}`)}
                              style={{
                                background: "#eff6ff",
                                border: "none",
                                borderRadius: 7,
                                width: 32,
                                height: 32,
                                cursor: "pointer",
                                color: "#2563eb",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                              }}
                            >
                              <EyeOutlined />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "center",
                  background: "#fafbff",
                }}
              >
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaidUsers;
